import { EventEmitter } from 'events';
import { IClient } from '@fluidframework/protocol-definitions';
import { AttachState } from '@fluidframework/container-definitions';
import { ExternalContainer, ExternalAudience } from '@fluidx/office-fluid-types';
import { PostMessageHandlers, PostMessageSender } from '../Shared/PostMessage';
import {
  SetAudienceMembersMessage,
  EmitAudienceEventMessage,
  SetConnectedMessage,
  SetClientIdMessage,
  EmitContainerEventMessage,
  CloseMessage,
  SetAutoReconnectMessage,
  ForceReadonlyMessage,
  ContainerAttachMessage,
  GetAbsoluteUrlMessage,
  GetAbsoluteUrlReturnMessage
} from '../MessageTypes';
import { IFrameProxy } from './IFrameProxy';
import { objectToMap } from '../Shared/MapUtils';
import { IRequest } from '@fluidframework/core-interfaces';
import { IResolvedUrl } from '@fluidframework/driver-definitions';
import { deserializeError } from '@fluidx/utilities';
import { Disposable } from '../Shared/Disposable';

/**
 * The audience proxy tries to mimic the actual Audience behavior inside the IFrame
 */
class AudienceProxy extends EventEmitter implements ExternalAudience, IFrameProxy, Disposable {
  private members = new Map<string, IClient>();

  getMembers(): Map<string, IClient> {
    return this.members;
  }

  public dispose() {
    this.removeAllListeners();
  }

  public handlers: PostMessageHandlers<string> = {
    ['SetAudienceMembers']: (message) => {
      this.members = objectToMap((message as SetAudienceMembersMessage).payload.value);
    },
    ['EmitAudienceEvent']: (message) => {
      const { event, args } = (message as EmitAudienceEventMessage).payload;
      this.emit(event, ...args);
    }
  };
}

/**
 * The container proxy tries to mimic the actual Container behavior inside the IFrame
 */
class ContainerProxy extends EventEmitter implements ExternalContainer, IFrameProxy, Disposable {
  private audienceProxy: AudienceProxy = new AudienceProxy();
  private connectedProxy = false;
  private currentAttachState = AttachState.Detached;
  private clientIdProxy: string | undefined = undefined;
  private closedProxy = false;
  private resolvedUrlProxy: IResolvedUrl | undefined;

  constructor(private readonly sender: PostMessageSender) {
    super();
  }

  close(): void {
    const message: CloseMessage = {
      type: 'Close'
    };
    this.sender.send(message);
  }

  setAutoReconnect(reconnect: boolean): void {
    const message: SetAutoReconnectMessage = {
      type: 'SetAutoReconnect',
      payload: { reconnect }
    };
    this.sender.send(message);
  }

  forceReadonly(readonly: boolean): void {
    const message: ForceReadonlyMessage = {
      type: 'ForceReadonly',
      payload: { readonly }
    };
    this.sender.send(message);
  }

  async attach(request: IRequest): Promise<void> {
    this.currentAttachState = AttachState.Attaching;

    const message: ContainerAttachMessage = {
      type: 'ContainerAttach',
      payload: { request }
    };
    const reply = await this.sender.sendWithReturn(message);
    if (reply.error) {
      this.currentAttachState = AttachState.Detached;
      throw reply.error;
    } else {
      this.currentAttachState = AttachState.Attached;
      this.resolvedUrlProxy = reply.payload as IResolvedUrl;
    }
  }

  async getAbsoluteUrl(relativeUrl: string): Promise<string | undefined> {
    const message: GetAbsoluteUrlMessage = {
      type: 'GetAbsoluteUrl',
      payload: { relativeUrl }
    };
    const reply = (await this.sender.sendWithReturn(message)) as GetAbsoluteUrlReturnMessage;
    if (reply.error) {
      throw deserializeError(reply.payload.error || 'Failed to get absolute url');
    }
    return reply.payload.absoluteUrl;
  }

  serialize(): string {
    throw new Error('Serialize is not supported in iframe');
  }

  public get resolvedUrl(): IResolvedUrl | undefined {
    return this.resolvedUrlProxy;
  }

  public get connected(): boolean {
    return this.connectedProxy;
  }

  public get attachState(): AttachState {
    return this.currentAttachState;
  }

  public get clientId(): string | undefined {
    return this.clientIdProxy;
  }

  public get audience(): ExternalAudience {
    return this.audienceProxy;
  }

  public get closed(): boolean {
    return this.closedProxy;
  }

  public dispose = () => {
    this.audienceProxy.dispose();
    this.removeAllListeners();
  };

  public handlers: PostMessageHandlers<string> = {
    ['SetConnected']: (message) => {
      this.connectedProxy = (message as SetConnectedMessage).payload.value;
    },
    ['SetClientId']: (message) => {
      this.clientIdProxy = (message as SetClientIdMessage).payload.value;
    },
    ['EmitContainerEvent']: (message) => {
      const { event, args } = (message as EmitContainerEventMessage).payload;

      switch (event) {
        case 'connected':
          this.connectedProxy = true;
          this.clientIdProxy = args?.[0] as string; // clientId
          break;
        case 'disconnected':
          this.connectedProxy = false;
          break;
        case 'closed':
          this.closedProxy = true;
          break;
      }

      if (args) {
        let convertedArgs: any[] = [];
        args.forEach((value: any) => {
          convertedArgs.push(value === null ? undefined : value);
        });

        this.emit(event, ...convertedArgs);
      } else {
        this.emit(event);
      }
    },
    ...this.audienceProxy.handlers
  };
}

/**
 * Initialize behaviors for all known container interfaces
 */
export function getContainerInterface(sender: PostMessageSender): ExternalContainer & IFrameProxy {
  return new ContainerProxy(sender);
}
